import { Component, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatDialog, MatTableDataSource } from '@angular/material';

import { Subscription, of, forkJoin } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';

import { ConfigurationCardComponent, GroupWithAssociations, AccessGroupEvent } from './components/configuration-card.component';

import { ConfigurationSummary } from 'src/app/models/configuration-summary.model';
import { UserDetailData } from 'src/app/models/user-detail-data.model';
import { UserGroup } from 'src/app/models/user-group.model';

import { AuthorizationService } from 'src/app/core/services/data/request/auth.service';
import { ConfigurationAccessService } from 'src/app/core/services/data/request/configuration-access.service';

import { CustomSnackbarService } from 'src/app/core/services/helper/custom-snackbar.service';
import { AdminDataService } from 'src/app/core/services/data/data.service';

@Component({
  selector: 'app-configuration',
  template: `
  <div *ngIf="isLoading" class="loading-indicator">
    <mat-spinner diameter="40"></mat-spinner>
  </div>
  <app-configuration-card
    [dataSource]="dataSource"
    [displayedColumns]="displayedColumns"
    [accessGroups]="accessGroups"
    [users]="users"
    [userGroups]="userGroups"   
    (accessGroupEvent)="onAccessGroupEvent($event)"
    (refreshEmitter)="refreshData()"
    (userClick)="onUserClick($event)"
    (userGroupClick)="onUserGroupClick($event)"
    (configurationClick)="onConfigurationClick($event)"
    (loadAccessGroupsRequest)="loadAccessGroups()"
    (deleteAccessGroupRequest)="deleteAccessGroup($event)">
  </app-configuration-card>
  `,
  styles: [`
    .loading-indicator {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 9999;
    }
  `]
})

export class ConfigurationContainerComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'configurationName',
    'configurationVersion',
    'configurationSchemaVersion',
    'modifiedDate'
  ];

  dataSource = new MatTableDataSource<ConfigurationSummary>([]);
  accessGroups: GroupWithAssociations[] = [];
  users: UserDetailData[] = [];
  userGroups: UserGroup[] = [];
  isLoading = false;
  
  // Add these new flags to track states
  private hasLoadedInitialData = false;
  private accessGroupsLoading = false;
  
  private subscriptions: Subscription[] = [];  

  @ViewChild(ConfigurationCardComponent) childView: ConfigurationCardComponent;

  constructor(
    public snackbarService: CustomSnackbarService,
    private authService: AuthorizationService,
    private configAccessService: ConfigurationAccessService,
    private adminDataService: AdminDataService,
    private changeDetectorRef: ChangeDetectorRef,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      // Use setTimeout to push the initialization to the next change detection cycle
      setTimeout(() => {
        // Only load if there's no data yet
        if (!this.dataSource.data || this.dataSource.data.length === 0) {
          this.loadInitialData();
        }
      }, 0);

      const configSub = this.adminDataService.configurationSummaries.subscribe(configs => {
        if (configs && configs.length > 0) {
          this.dataSource.data = configs;          
          this.updateConfigurationGroups();
          this.changeDetectorRef.markForCheck();
        }
      });

      const userDeletedSub = this.adminDataService.userDeleted.subscribe(deleted => {
        if (deleted) {
          this.loadAccessGroups();
          this.adminDataService['_userDeleted'].next(false);
        }
      });
      
      // Use markForCheck instead of detectChanges
      const userGroupsSub = this.adminDataService.userGroupDetails.subscribe(userGroups => {
        if (userGroups && userGroups.length > 0) {
          this.userGroups = userGroups;
          this.changeDetectorRef.markForCheck();
        }
      });
      
      this.subscriptions.push(configSub, userDeletedSub, userGroupsSub);
    }
  }

  // Modified loadInitialData method with proper caching
  loadInitialData(): void {
    // Skip if already loaded
    if (this.hasLoadedInitialData) {
      console.log('Initial data already loaded, skipping');
      return;
    }
    
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();
    
    const configSub = this.adminDataService.configurationSummaries.pipe(
      take(1),
      catchError(error => {
        console.error('Error loading configuration data:', error);
        this.snackbarService.showError('Failed to load configuration data');
        return of([]);
      })
    ).subscribe(configurations => {
      this.dataSource.data = configurations || [];
      
      // Use array syntax for forkJoin
      forkJoin([
        this.adminDataService.userDetails.pipe(take(1)),
        this.adminDataService.userGroupDetails.pipe(take(1))
      ]).pipe(
        catchError(error => {
          console.error('Error loading user data:', error);
          this.snackbarService.showError('Failed to load user data');
          return of([[], []]);
        })
      ).subscribe(([users, userGroups]) => {
        this.users = users || [];
        this.userGroups = userGroups || [];
        
        // Only load access groups after users and user groups are loaded
        this.loadAccessGroups();
        
        // Mark initial data as loaded
        this.hasLoadedInitialData = true;
      });
    });
    
    this.subscriptions.push(configSub);
    this.adminDataService.refreshConfigurationState();
    
    const safetyTimeout = setTimeout(() => {
      if (this.isLoading) {
        console.warn('[Container] Safety timeout triggered - clearing loading state');
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      }
    }, 15000);
    
    this.subscriptions.push({
      unsubscribe: () => clearTimeout(safetyTimeout)
    } as Subscription);
  }

  loadAccessGroups(): void {
    if (this.accessGroupsLoading) {
      console.log('[Container] Access groups already loading, skipping duplicate request');
      return;
    }
    
    if (!this.isLoading) {
      this.isLoading = true;
      this.changeDetectorRef.markForCheck();
    }
    
    this.accessGroupsLoading = true;
    console.log('[Container] Loading access groups');
    
    const subscription = this.configAccessService.getAllAccessGroups().pipe(
      map(groups => {
        return groups.map(group => ({
          ...group,
          isDefault: group.name === 'QRA Default',
          isUnassigned: group.name === 'Unassigned',
          canEdit: !['Unassigned', 'QRA Default'].includes(group.name),
          expanded: false,
          visible: true,
          hasConfigurations: false
        } as GroupWithAssociations));
      }),
      catchError(error => {
        console.error('Error loading access groups:', error);
        this.snackbarService.showError('Failed to load access groups');
        this.isLoading = false;
        this.accessGroupsLoading = false;
        this.changeDetectorRef.markForCheck();
        return of([]);
      }),
      finalize(() => {
        this.accessGroupsLoading = false;
      })
    ).subscribe(groups => {
      this.accessGroups = groups;
      this.accessGroups.forEach(group => {
        group.userIds = [];
        group.userGroupIds = [];
        group.configurationUuids = [];
      });
      
      this.updateConfigurationGroups();
      
      if (this.accessGroups.length === 0) {
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
        return;
      }
      
      this.loadAccessGroupAssociations();
    });
    
    this.subscriptions.push(subscription);
  }

  loadAccessGroupAssociations(): void {
    const totalRequests = this.accessGroups.length * 3; 
    let completedRequests = 0;
    
    if (this.accessGroups.length === 0) {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }    
   
    this.accessGroups.forEach(group => {
      const userSub = this.configAccessService.getUsersInAccessGroup(group.uuid).pipe(
        catchError(error => {
          console.error(`Error loading users for group ${group.name}:`, error);
          return of([]);
        }),
        finalize(() => {})
      ).subscribe(
        users => {
          group.userIds = users || [];
          checkComplete();
        },
        error => {
          console.error(`Error in user subscription for group ${group.name}:`, error);
          checkComplete();
        }
      );
      
      const userGroupSub = this.configAccessService.getUserGroupsInAccessGroup(group.uuid).pipe(
        catchError(error => {
          console.error(`Error loading user groups for group ${group.name}:`, error);
          return of([]);
        }),
        finalize(() => {})
      ).subscribe(
        userGroups => {
          group.userGroupIds = userGroups || [];
          checkComplete();
        },
        error => {
          console.error(`Error in user group subscription for group ${group.name}:`, error);
          checkComplete();
        }
      );
      
      const configSub = this.configAccessService.getConfigurationsInAccessGroup(group.uuid).pipe(
        catchError(error => {
          console.error(`Error loading configurations for group ${group.name}:`, error);
          return of([]);
        }),
        finalize(() => {})
      ).subscribe(
        configs => {
          group.configurationUuids = configs || [];
          group.hasConfigurations = (configs || []).length > 0;
          
          this.updateConfigurationGroups();          
          checkComplete();
        },
        error => {
          console.error(`Error in configuration subscription for group ${group.name}:`, error);
          checkComplete();
        }
      );
      
      this.subscriptions.push(userSub, userGroupSub, configSub);
    });
    
    const checkComplete = () => {
      completedRequests++;       
      if (completedRequests === totalRequests) {        
        this.accessGroups.sort((a, b) => {
          if (a.isDefault) return -1;
          if (b.isDefault) return 1;
          if (a.isUnassigned) return -1;
          if (b.isUnassigned) return 1;
          return a.name.localeCompare(b.name);
        });
        
        this.updateConfigurationGroups();
        
        this.isLoading = false;
        // Use markForCheck instead of detectChanges
        this.changeDetectorRef.markForCheck();
      }
    };
  }
   
  updateConfigurationGroups(): void {
    if (!this.dataSource || !this.dataSource.data || !this.accessGroups) {
      return;
    }
    
    this.dataSource.data.forEach(config => {
      config['inAccessGroups'] = this.accessGroups.filter(group =>
        group.configurationUuids && group.configurationUuids.includes(config.configurationId)
      );
    });
    
    this.changeDetectorRef.markForCheck();
  }

  refreshData(): void {   
    // Reset state flags
    this.hasLoadedInitialData = false;
    this.accessGroupsLoading = false;
    
    this.dataSource.data = [];
    this.accessGroups = [];
    this.users = [];
    this.userGroups = [];
    
    this.adminDataService.refreshConfigurationState();
    
    // Use setTimeout to avoid change detection issues
    setTimeout(() => {
      this.loadInitialData();
    }, 0);
  }

  onAccessGroupEvent(event: AccessGroupEvent): void {
    this.loadAccessGroups();
  }

  onUserClick(userId: number): void {
    console.log(`User clicked: ${userId}`);
    const userSub = this.adminDataService.userDetails.pipe(take(1)).subscribe(users => {
      const user = users.find(u => u.userId === userId);
      if (user) {
        this.adminDataService.setCurrentSelectedUserDetails(user);
      }
    });
    
    this.subscriptions.push(userSub);
  }

  onUserGroupClick(userGroupId: number): void {
    const groupSub = this.adminDataService.userGroupDetails.pipe(take(1)).subscribe(groups => {
      const userGroup = groups.find(g => g.id === userGroupId);
      if (userGroup) {
        this.adminDataService.setCurrentSelectedUserGroupDetails(userGroup);
      }
    });
    
    this.subscriptions.push(groupSub);
  }

  onConfigurationClick(configUuid: string): void {
    console.log(`Configuration clicked: ${configUuid}`);
    this.adminDataService.setCurrentSelectedConfigurationDetails(configUuid);
  }
  
  deleteAccessGroup(groupUuid: string): void {
    if (!groupUuid) {
      return;
    }
    
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();    
    
    const subscription = this.configAccessService.deleteAccessGroup(groupUuid).pipe(
      catchError(error => {
        console.error('Error deleting access group:', error);
        this.snackbarService.showError('Failed to delete access group');
        return of(null);
      }),
      finalize(() => {
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe(
      () => {
        this.snackbarService.showSuccess('Access group deleted successfully');
        this.loadAccessGroups();
      }
    );
    
    this.subscriptions.push(subscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}